import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { Feature } from 'src/WebApiController';

/**
 * Hook that derives currency conversion state from the purchase form.
 *
 * - `targetCurrency`: the seller account's native currency (the one
 *   the purchase was converted to and that gets saved to the DB).
 * - `originalCurrency`: the currency the purchase was originally made
 *   in (e.g. the foreign currency). `null` when the PO has no FX data.
 * - `viewingCurrency`: whichever currency the form is currently
 *   displaying — equals `currencyCode.value` on the form.
 * - `isViewingInOriginalCurrency`: `true` when the user has toggled away from the
 *   target currency (i.e. viewing in the original currency).
 * - `conversionRate`: the rate from the PO (`originalCurrency → targetCurrency`).
 * - `isFeatureEnabled`: whether the PurchaseOrderFXConversionDisplay feature
 *   flag is on for this user/account.
 * - `isConvertedPurchase`: convenience flag — feature is on AND the PO has
 *   both an originalCurrencyCode and a conversionRate.
 */
export function usePurchaseCurrencyConversion() {
  const { loginContext } = useAppContext();
  const {
    watch,
    formState: { defaultValues },
  } = useFormContext<PurchaseOrderDetailsInput>();

  const isFeatureEnabled = useUserHasFeature(
    Feature.PurchaseOrderFXConversionDisplay
  );

  const currencyCode = watch('currencyCode');
  const originalCurrencyCode = watch('originalCurrencyCode');
  const conversionRate = watch('conversionRate');

  // The target currency is the account's native currency, which is
  // what the form was originally loaded with (from defaultValues).
  const targetCurrency =
    defaultValues?.currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const viewingCurrency =
    currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const isViewingInOriginalCurrency = viewingCurrency !== targetCurrency;

  const isConvertedPurchase =
    isFeatureEnabled &&
    originalCurrencyCode != null &&
    conversionRate != null &&
    conversionRate > 0;

  return {
    isViewingInOriginalCurrency,
    originalCurrency: originalCurrencyCode,
    targetCurrency,
    viewingCurrency,
    conversionRate,
    isFeatureEnabled,
    isConvertedPurchase,
  };
}
